import { Query } from 'graphql/generated/graphqlTypes';
import { generateMockAccessGrant } from 'mocks/graphqlMocks/accessGrant';
import { generateMockVisitorGroup } from 'mocks/graphqlMocks/visitorGroup';

export function mockedGetAllVisitorsGroupsWithoutNestedResolversData(
  getVisitorGroupsForTenant: Query['getVisitorGroupsForTenant']
): Pick<Query, 'getVisitorGroupsForTenant'> {
  return { getVisitorGroupsForTenant };
}

const officeHoursSchedule = {
  __typename: 'WeekSchedule' as const,
  weekDays: [{ __typename: 'WeekScheduleDay' as const, dayName: 'monday', from: '09:00', to: '17:00' }]
};

// Backs the User groups tab — 6 groups, one per meaningful combination. Four things to know:
//
//   - `members` is stitched client-side by matching `memberIds` against the visitors returned by
//     GET_VISITORS_WITHOUT_NESTED_RESOLVERS, which serves `mockVisitorsInstallersAndCleaners`
//     (10 visitors), NOT the Users overview list. An id outside that set is silently dropped and
//     the Users column would read 0. Every id below is from that array.
//   - the three access filters are AND-ed (unlike the users role/origin filters, which are OR-ed),
//     so "Empty Group" — the only group with no users, no schedule and no devices — is the only
//     row matching all three at once, and each filter alone also returns a predictable set.
//   - office mode and the schedule live on the access grant, not on the group.
//   - `access` is complete only when a group has users AND devices AND a schedule.
//
// Verified distribution (6 groups):
//   origin      — Preset 2 (Installers, Empty Group) · Atlas 2 (Cleaning Crew, Internal Team)
//                 · Entra ID 1 (Employees) · Unknown 1 (Cognito Team, type EXTERNAL_VISITORS)
//   noUsers     — 2 (Empty Group, Entra-less Internal Team)
//   noSchedule  — 3 (Cleaning Crew, Empty Group, Internal Team)
//   noDevices   — 2 (Empty Group, Cognito Team)
//   all three   — 1 (Empty Group)
//   office mode — allowed 2 (Installers, Employees) · not allowed 4
//   access      — complete 2 (Installers, Employees) · incomplete 4
//   maxMembers  — only Cleaning Crew (1/10) and Employees (3/50) show the "n/max" form
export const mockedGetAllVisitorsGroupsWithoutNestedResolversDataForQuery =
  mockedGetAllVisitorsGroupsWithoutNestedResolversData([
    // Preset origin · users + schedule + devices -> access complete · office mode on
    generateMockVisitorGroup({
      id: 'mockedIdInstallers',
      name: 'Installers',
      type: 'INSTALLERS',
      memberIds: ['mockVisitorId1', 'mockVisitorInstaller2'],
      accessGrants: [
        generateMockAccessGrant({
          id: 'mockedAccessGrantIdInstallers',
          accessSchedule: officeHoursSchedule,
          isOfficeModeEnabled: true,
          locationIds: ['TestGroup1'],
          doorIds: ['mockedDoorId1']
        })
      ]
    }),
    // Atlas origin · users + devices but NO schedule · the "n/max" members form
    generateMockVisitorGroup({
      id: 'mockedIdCleaners',
      name: 'Cleaning Crew',
      type: 'CO_CONTRACTORS',
      maxNumberOfMembers: 10,
      memberIds: ['mockVisitorId2'],
      accessGrants: [
        generateMockAccessGrant({
          id: 'mockedAccessGrantIdCleaners',
          doorIds: ['mockedDoorId2']
        })
      ]
    }),
    // Entra ID origin · everything set up · office mode on · 3 members against a max of 50
    generateMockVisitorGroup({
      id: 'mockedIdEmployees',
      name: 'Employees',
      type: 'AZURE_AD',
      maxNumberOfMembers: 50,
      memberIds: ['mockVisitorId2', 'mockVisitorId8', 'mockVisitorId11'],
      accessGrants: [
        generateMockAccessGrant({
          id: 'mockedAccessGrantIdEmployees',
          accessSchedule: officeHoursSchedule,
          isOfficeModeEnabled: true,
          locationIds: ['TestGroup2'],
          doorIds: ['mockedDoorId1']
        })
      ]
    }),
    // Atlas origin · users + schedule but NO devices
    generateMockVisitorGroup({
      id: 'mockedIdCognitoTeam',
      name: 'Cognito Team',
      type: 'CO_CONTRACTORS',
      memberIds: ['mockVisitorId9', 'mockVisitorId10'],
      accessGrants: [
        generateMockAccessGrant({
          id: 'mockedAccessGrantIdCognitoTeam',
          accessSchedule: officeHoursSchedule
        })
      ]
    }),
    // Atlas origin · devices but NO users and NO schedule
    generateMockVisitorGroup({
      id: 'mockedIdInternalTeam',
      name: 'Internal Team',
      type: 'CO_CONTRACTORS',
      accessGrants: [
        generateMockAccessGrant({
          id: 'mockedAccessGrantIdInternalTeam',
          doorIds: ['mockedDoorId2']
        })
      ]
    }),
    // Preset origin · nothing set up — the only row matching all three access filters at once
    generateMockVisitorGroup({
      id: 'mockedIdAdmins',
      name: 'Empty Group',
      type: 'ADMINISTRATORS',
      accessGrants: [generateMockAccessGrant({ id: 'mockedAccessGrantIdAdmins' })]
    })
  ]);
